import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Compass, 
  Map, 
  BookOpen, 
  Archive, 
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  Shield,
  Home,
  Megaphone,
  BarChart3,
  Gift,
  Sparkles,
  UserCog,
  ExternalLink
} from "lucide-react";
import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";

const NAV_ITEMS = [
  { path: "/compass", label: "Compass", icon: Compass },
  { path: "/discover", label: "Discover", icon: Map },
  { path: "/journal", label: "Journal", icon: BookOpen },
  { path: "/vault", label: "Vault", icon: Archive },
];

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/compass" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="/compass-logo.png" 
              alt="Compass" 
              className="h-10 w-10 object-contain"
            />
            <span className="font-heading text-lg text-foreground hidden sm:block">Compass</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-foreground hover:text-foreground hover:bg-primary/10"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  <item.icon className={`w-4 h-4 ${isActive ? "text-white" : ""}`} strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Admin Link - Only visible to admin users */}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
                  location.pathname.startsWith("/admin")
                    ? "bg-primary/20 text-primary border border-primary/30"
                    : "text-primary/80 hover:text-primary hover:bg-primary/10"
                }`}
                data-testid="nav-admin"
              >
                <Shield className="w-4 h-4" strokeWidth={1.5} />
                Admin
              </Link>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-primary/10"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="w-4 h-4" style={{ color: '#ffffff', stroke: '#ffffff' }} />
                  </div>
                  <span className="text-sm text-foreground hidden sm:block">
                    {user?.name || "User"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-primary border-white/10">
                <DropdownMenuItem 
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer hover:bg-white/10"
                  style={{ color: '#ffffff', fontWeight: 700 }}
                  data-testid="menu-profile"
                >
                  <User className="w-4 h-4 mr-2" style={{ color: '#ffffff', stroke: '#ffffff' }} />
                  Profile
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem 
                    onClick={() => navigate("/admin")}
                    className="cursor-pointer hover:bg-white/10"
                    style={{ color: '#ffffff', fontWeight: 700 }}
                    data-testid="menu-admin"
                  >
                    <Settings className="w-4 h-4 mr-2" style={{ color: '#ffffff', stroke: '#ffffff' }} />
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/20" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-300 hover:bg-white/10"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-primary/20 animate-fade-in">
            <div className="flex flex-col gap-2">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-300 ${
                      isActive
                        ? "bg-primary text-white"
                        : "text-foreground hover:text-foreground hover:bg-primary/10"
                    }`}
                  >
                    <item.icon className={`w-5 h-5 ${isActive ? "text-white" : ""}`} strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
              
              {/* Admin Link - Mobile */}
              {user?.role === "admin" && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-300 ${
                    location.pathname.startsWith("/admin")
                      ? "bg-primary/20 text-primary border border-primary/30"
                      : "text-primary/80 hover:text-primary hover:bg-primary/10"
                  }`}
                >
                  <Shield className="w-5 h-5" strokeWidth={1.5} />
                  Admin
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const AdminNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Theme toggle - now always available via ThemeProviderWithRouter
  const { theme, toggleTheme } = useTheme();

  const ADMIN_NAV_ITEMS = [
    { path: "/admin", label: "Dashboard", icon: Home },
    { path: "/admin/members", label: "Members", icon: User },
    { path: "/admin/courses", label: "Courses", icon: BookOpen },
    { path: "/admin/signals", label: "Signals", icon: Sun },
    { path: "/admin/resources", label: "Resources", icon: Archive },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/admin/announcements", label: "Announce", icon: Megaphone },
    { path: "/admin/trial-codes", label: "Trials", icon: Gift },
    { path: "/admin/pathfinder", label: "AI", icon: Sparkles },
    { path: "/admin/audit", label: "Audit", icon: UserCog },
    { path: "/admin/community", label: "Mod", icon: Shield },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-3" data-testid="admin-nav-logo">
              <img 
                src="/compass-logo.png" 
                alt="Compass" 
                className="h-10 w-10 object-contain"
              />
              <span className="font-heading text-lg text-foreground hidden sm:block">Admin</span>
            </Link>

            {/* Desktop Nav - Hidden on mobile, scrollable on tablet */}
            <div className="hidden lg:flex items-center gap-1">
              {ADMIN_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-colors duration-300 ${
                      isActive
                        ? "bg-primary"
                        : "text-foreground hover:text-foreground hover:bg-primary/10"
                    }`}
                    style={isActive ? { color: '#ffffff', fontWeight: 700 } : {}}
                  >
                    <item.icon 
                      className="w-3.5 h-3.5" 
                      strokeWidth={1.5} 
                      style={isActive ? { color: '#ffffff', stroke: '#ffffff' } : {}}
                    />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-primary/10 transition-colors"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? (
                <Sun className="w-4 h-4 text-[#a69373]" />
              ) : (
                <Moon className="w-4 h-4 text-[#1a3a30]" />
              )}
            </button>

            {/* View Site Button - Always visible */}
            <Link 
              to="/compass" 
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm bg-primary transition-colors duration-300 hover:bg-[#6a8e6a]"
              style={{ color: '#ffffff', fontWeight: 700 }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">View Site</span>
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-primary/10 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>

            {/* Desktop user menu */}
            <div className="hidden lg:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-primary/10">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="w-4 h-4" style={{ color: '#ffffff', stroke: '#ffffff' }} />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-primary border-white/10">
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-300 hover:bg-white/10">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {ADMIN_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs transition-colors duration-300 ${
                      isActive
                        ? "bg-primary text-white"
                        : "text-white/70 hover:bg-primary/10"
                    }`}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
            {/* Theme toggle in mobile menu */}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-white/70 hover:bg-primary/10 transition-colors mb-2"
            >
              {theme === "dark" ? (
                <>
                  <Sun className="w-4 h-4" />
                  Switch to Light Mode
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4" />
                  Switch to Dark Mode
                </>
              )}
            </button>
            <button
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
